import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, FileCheck2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AREAS, CHAIN_STATUSES, OWNERSHIP_STATUSES, PROOF_TYPES, BUYER_TIMELINES, PROPERTY_TYPES, BEDROOMS } from "@/lib/constants";
import { Field, Select, Chips, SubmitButton } from "@/components/site/FormBits";
import { AccountGate } from "@/components/site/AccountGate";

const EMPTY = { budget_min: "", budget_max: "", target_areas: [], property_types: [], bedrooms_min: "", chain_status: "",
  ownership_status: "", proof_type: "", timeline: "", notes: "" };

const ProofUpload = ({ file, setFile }) => {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/proof", fd);
      setFile(data);
      toast.success("Document uploaded securely");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <label className="flex items-center gap-4 border border-dashed border-[#C5BCAB] bg-[#F7F6F2] p-5 cursor-pointer hover:border-[#9F7E44] transition-colors">
      {file ? <FileCheck2 className="h-6 w-6 text-[#065F46]" /> : <Upload className="h-6 w-6 text-[#9F7E44]" />}
      <span className="text-sm">
        <span className="block font-medium" data-testid="buyer-form-file-name">{busy ? "Uploading…" : file ? file.original_filename : "Upload AIP or proof of funds"}</span>
        <span className="block text-xs text-[#847F77]">PDF, JPG, PNG or WEBP · max 10MB · only visible to us</span>
      </span>
      <input data-testid="buyer-form-file-input" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="sr-only" onChange={onPick} disabled={busy} />
    </label>
  );
};

const BuyerForm = () => {
  const nav = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please upload your AIP or proof of funds");
    setLoading(true);
    try {
      await api.post("/buyer-applications", {
        ...f, budget_min: f.budget_min ? Number(f.budget_min) : null, budget_max: Number(f.budget_max),
        bedrooms_min: f.bedrooms_min || null, proof_file_id: file.id,
      });
      toast.success("Thank you. Your buyer profile is with us for review.");
      nav("/portal");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#E4E1D9] p-6 sm:p-10 space-y-8" data-testid="buyer-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Minimum budget (£)"><input data-testid="buyer-form-budget-min" type="number" min="0" value={f.budget_min} onChange={set("budget_min")} className="field-input" placeholder="e.g. 400000" /></Field>
        <Field label="Maximum budget (£)" required><input data-testid="buyer-form-budget-max" type="number" min="0" required value={f.budget_max} onChange={set("budget_max")} className="field-input" placeholder="e.g. 750000" /></Field>
      </div>
      <Field label="Areas of interest"><Chips options={AREAS} value={f.target_areas} onChange={(v) => setF({ ...f, target_areas: v })} testIdPrefix="buyer-form-area" /></Field>
      <Field label="Property types"><Chips options={PROPERTY_TYPES} value={f.property_types} onChange={(v) => setF({ ...f, property_types: v })} testIdPrefix="buyer-form-ptype" /></Field>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Minimum bedrooms"><Select testId="buyer-form-bedrooms" options={BEDROOMS} value={f.bedrooms_min} onChange={set("bedrooms_min")} placeholder="Any" /></Field>
        <Field label="When can you proceed?" required><Select testId="buyer-form-timeline" required options={BUYER_TIMELINES} value={f.timeline} onChange={set("timeline")} /></Field>
      </div>
      <div className="h-px bg-[#E4E1D9]" />
      <p className="eyebrow">Your position</p>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Chain status" required><Select testId="buyer-form-chain-status" required options={CHAIN_STATUSES} value={f.chain_status} onChange={set("chain_status")} /></Field>
        <Field label="Current property ownership" required><Select testId="buyer-form-ownership-status" required options={OWNERSHIP_STATUSES} value={f.ownership_status} onChange={set("ownership_status")} /></Field>
      </div>
      <Field label="Proof type" required><Select testId="buyer-form-proof-type" required options={PROOF_TYPES} value={f.proof_type} onChange={set("proof_type")} /></Field>
      <Field label="AIP / proof of funds document" required><ProofUpload file={file} setFile={setFile} /></Field>
      <Field label="Anything else?"><textarea data-testid="buyer-form-notes" rows={3} value={f.notes} onChange={set("notes")} className="field-input h-auto py-3" /></Field>
      <SubmitButton loading={loading} testId="buyer-form-submit-btn" className="w-full sm:w-auto">Submit buyer profile</SubmitButton>
    </form>
  );
};

export default function BuyPage() {
  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-20 grid lg:grid-cols-12 gap-12" data-testid="buy-page">
      <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start">
        <p className="eyebrow">For buyers</p>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-tight">Ready to buy? Skip the chain.</h1>
        <p className="mt-6 text-[#57534E] leading-relaxed">
          Tell us what you're looking for and show us you can proceed. Once we've reviewed your proof of funds, we'll introduce you to suitable sellers who want a quick, clean sale.
        </p>
        <ul className="mt-8 space-y-3 text-sm">
          {["Agreement in Principle or proof of funds", "Your chain status", "Your current property ownership"].map((t) => (
            <li key={t} className="flex gap-3"><span className="mt-1.5 h-1.5 w-1.5 bg-[#9F7E44] shrink-0" />{t}</li>
          ))}
        </ul>
        <p className="mt-8 text-xs text-[#847F77] leading-relaxed">Your documents are stored privately and seen only by us. They are never shared with sellers.</p>
      </aside>
      <div className="lg:col-span-8"><AccountGate next="/buy"><BuyerForm /></AccountGate></div>
    </div>
  );
}
